import { useMemo } from 'react';
import type { schemas } from '@gonfalon/openapi/types';
import { useApprovalRequestSettings } from '@gonfalon/rest-api/queries/approvalSettings';

import { usePendingChangesFeatureAccess } from '#hooks/usePendingChangesFeatureAccess';

type UseAiConfigApprovalSettingsProps = {
  projectKey: string;
  environmentKey: string;
};

type Nullable<T> = T | null;

export function useAiConfigApprovalSettings({ projectKey, environmentKey }: UseAiConfigApprovalSettingsProps) {
  const isApprovalsEnabled = usePendingChangesFeatureAccess().approvals();

  const { data } = useApprovalRequestSettings(
    {
      header: { 'LD-API-Version': 'beta' },
      projectKey,
      query: {
        resourceKind: 'aiconfig',
        expand: 'default,strict',
      },
    },
    {
      enabled: isApprovalsEnabled,
    },
  );

  const approvalSettings: Nullable<{
    currentEnvironment: schemas['ApprovalRequestSetting'];
    allEnvironments: schemas['ApprovalRequestSetting'];
  }> = useMemo(() => {
    if (!isApprovalsEnabled) {
      return null;
    }

    const currentEnvironment = data?.aiconfig?.environments?.[environmentKey] ?? data?.aiconfig?._default;
    const allEnvironments = data?.aiconfig?._strict;

    if (!currentEnvironment || !allEnvironments) {
      return null;
    }

    return { currentEnvironment, allEnvironments };
  }, [data, environmentKey, isApprovalsEnabled]);

  return approvalSettings;
}
